type LineItemQuantityEl = HTMLElement & {
  _changeLineItemQuantity?: (
    lineKey: string,
    newQuantity: number,
    oldQuantity?: number,
  ) => void | Promise<unknown>;
};

const triggerChange = async (
  inputEl: HTMLInputElement,
  oldQuantity?: number,
): Promise<void> => {
  const el = inputEl.closest("line-item-quantity");
  const host: LineItemQuantityEl | null = el instanceof HTMLElement ? el : null;
  const lineKey = inputEl.getAttribute("data-line-key");
  const newQuantity = parseInt(inputEl.value || "", 10);
  const prev =
    typeof oldQuantity === "number"
      ? oldQuantity
      : parseInt(inputEl.dataset.prevValue || inputEl.defaultValue || "", 10);

  if (host && typeof host._changeLineItemQuantity === "function" && lineKey) {
    const result = host._changeLineItemQuantity(
      lineKey,
      newQuantity,
      isNaN(prev) ? undefined : prev,
    );
    if (result instanceof Promise) await result;
  } else {
    inputEl.dispatchEvent(new Event("change", { bubbles: true }));
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
};

const parseInteger = (
  text: string | null | undefined,
  defaultValue: number,
): number => {
  const source = (text ?? "").trim();
  if (!/^-?\d+$/.test(source)) return defaultValue;
  const parsed = Number(source);
  return Number.isInteger(parsed) ? parsed : defaultValue;
};

const updateDecreaseButton = (input: HTMLInputElement): void => {
  const container = input.closest(".quantity-input-buttons");
  const decreaseBtn = container?.querySelector(".quantity-button.decrease");
  if (!(decreaseBtn instanceof HTMLButtonElement)) return;
  const current = parseInteger(input.value, 1);
  const min = parseInteger(input.min, 1);
  decreaseBtn.disabled = min === 1 ? current <= 0 : current <= min;
};

const quantityChangeHandler = () => {
  document.addEventListener("click", async (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(".quantity-button");
    if (!(button instanceof HTMLElement)) return;

    const wrapper = button.closest(".quantity-input-buttons");
    if (!wrapper) return;

    const inputEl = wrapper.querySelector(".quantity-input");
    if (!(inputEl instanceof HTMLInputElement)) return;

    const step = parseInteger(inputEl.step, 1);
    const min = parseInteger(inputEl.min, 1);
    const max = inputEl.max
      ? parseInteger(inputEl.max, Number.POSITIVE_INFINITY)
      : Number.POSITIVE_INFINITY;
    const current = parseInteger(inputEl.value, min);
    const increasing = button.classList.contains("increase");
    let next: number;
    if (increasing) {
      next = Math.min(current + step, max);
    } else {
      next = min === 1 && current <= 1 ? 0 : Math.max(current - step, min);
    }

    if (increasing && current + step > max) {
      const lineItem = button.closest("[data-line-item]");
      const warning = lineItem?.querySelector(".line-item__warning");
      if (warning) {
        warning.classList.add("line-item__visible");
        setTimeout(() => warning.classList.remove("line-item__visible"), 2000);
      }
      return;
    }
    if (next === current) return;

    const oldQuantity = parseInt(
      inputEl.dataset.prevValue || inputEl.defaultValue || inputEl.value || "",
      10,
    );

    inputEl.value = String(next);
    updateDecreaseButton(inputEl);
    await triggerChange(inputEl, isNaN(oldQuantity) ? undefined : oldQuantity);
  });
};

document.addEventListener("DOMContentLoaded", () => {
  quantityChangeHandler();
  document
    .querySelectorAll<HTMLInputElement>(".quantity-input")
    .forEach(updateDecreaseButton);
});
